import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  useBookmarkFoldersQuery,
  useBookmarksQuery,
  useCreateBookmarkFolderMutation,
  useCreateBookmarkMutation,
  useUpdateBookmarkMutation,
  useDeleteBookmarkMutation,
  checkDuplicate,
  bookmarkQueryKeys,
} from '../hooks/queries/useBookmarkQueries';
import { useQueryClient } from '@tanstack/react-query';
import type { BookmarkFolder, Bookmark } from '@ext-bookmark/shared/types';
import BookmarkSidebar from '../components/bookmarks/BookmarkSidebar';
import BookmarkContent from '../components/bookmarks/BookmarkContent';
import BookmarkForm from '../components/bookmarks/BookmarkForm';
import { normalizeUrl, isValidUrl } from '@ext-bookmark/shared/utils';
import { backupFromBrowser, restoreToBrowser } from '../services/bookmark/backupRestoreService';

function buildSubfolders(folders: BookmarkFolder[], parentId: string | null): BookmarkFolder[] {
  return folders
    .filter((f) => f.parent_id === parentId)
    .sort((a, b) => a.position - b.position || (a.created_at < b.created_at ? -1 : 1));
}

const BookmarksPage: React.FC = () => {
  const { folderId } = useParams<{ folderId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const queryClient = useQueryClient();

  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [editingBookmark, setEditingBookmark] = useState<Bookmark | null>(null);
  const [backupRestoreError, setBackupRestoreError] = useState<string | null>(null);
  const [backupRestoreLoading, setBackupRestoreLoading] = useState(false);

  const { data: folders = [], isLoading: foldersLoading } = useBookmarkFoldersQuery(userId);
  const { data: bookmarks = [], isLoading: bookmarksLoading } = useBookmarksQuery(userId, folderId ?? null);

  const createFolderMutation = useCreateBookmarkFolderMutation(userId ?? '');
  const createBookmarkMutation = useCreateBookmarkMutation(userId ?? '', folderId ?? '');
  const updateBookmarkMutation = useUpdateBookmarkMutation(userId ?? '', folderId ?? '');
  const deleteBookmarkMutation = useDeleteBookmarkMutation(userId ?? '', folderId ?? '');

  const currentFolder: BookmarkFolder | null =
    folderId != null ? folders.find((f) => f.id === folderId) ?? null : null;
  const subfolders = buildSubfolders(folders, folderId ?? null);
  const bookmarksToShow = folderId != null ? bookmarks : [];

  const handleAddFolder = useCallback(
    (parentId?: string | null) => {
      const name = window.prompt('请输入文件夹名称', '书签栏');
      if (name?.trim()) {
        createFolderMutation.mutate(
          { name: name.trim(), parentId: parentId ?? null },
          {
            onSuccess: (folder) => {
              navigate(`/bookmarks/${folder.id}`);
            },
          }
        );
      }
    },
    [createFolderMutation, navigate]
  );

  const handleAddSubfolderFromContent = useCallback(
    (name: string) => {
      if (!userId) return;
      createFolderMutation.mutate(
        { name, parentId: folderId ?? null },
        {
          onSuccess: (folder) => {
            navigate(`/bookmarks/${folder.id}`);
          },
        }
      );
    },
    [userId, folderId, createFolderMutation, navigate]
  );

  const handleAddBookmark = useCallback(
    async (params: { name: string; url: string; note?: string }) => {
      if (!userId || !folderId) {
        setDuplicateError('请先选择一个文件夹');
        return;
      }
      const isDup = await checkDuplicate(folderId, params.name, normalizeUrl(params.url.trim()));
      if (isDup) {
        setDuplicateError('同文件夹下已存在相同名称与网址的书签');
        return;
      }
      setDuplicateError(null);
      createBookmarkMutation.mutate(
        { name: params.name, url: normalizeUrl(params.url), note: params.note },
        { onSuccess: () => {} }
      );
    },
    [userId, folderId, createBookmarkMutation]
  );

  const handleEditBookmark = useCallback((bookmark: Bookmark) => {
    setEditingBookmark(bookmark);
  }, []);

  const handleSaveEditBookmark = useCallback(
    async (data: { name: string; url: string; note?: string }) => {
      if (!editingBookmark) return;
      const url = normalizeUrl(data.url.trim());
      if (!isValidUrl(url)) return;
      const isDup = await checkDuplicate(
        editingBookmark.folderId ?? folderId ?? '',
        data.name.trim(),
        url,
        editingBookmark.id
      );
      if (isDup) {
        setDuplicateError('同文件夹下已存在相同名称与网址的书签');
        return;
      }
      setDuplicateError(null);
      updateBookmarkMutation.mutate(
        {
          bookmarkId: editingBookmark.id,
          params: { name: data.name, url, note: data.note },
        },
        { onSuccess: () => setEditingBookmark(null) }
      );
    },
    [editingBookmark, folderId, updateBookmarkMutation]
  );

  const handleDeleteBookmark = useCallback(
    (id: string) => {
      if (confirm('确定要删除这个书签吗？')) {
        deleteBookmarkMutation.mutate(id);
      }
    },
    [deleteBookmarkMutation]
  );

  const handleBackup = useCallback(async () => {
    if (!userId) return;
    setBackupRestoreLoading(true);
    setBackupRestoreError(null);
    const { error } = await backupFromBrowser(userId);
    setBackupRestoreLoading(false);
    if (error) {
      setBackupRestoreError(error);
    } else {
      queryClient.invalidateQueries({ queryKey: bookmarkQueryKeys.folders(userId) });
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
      alert('备份完成');
    }
  }, [userId, queryClient]);

  const handleRestore = useCallback(async () => {
    if (!userId) return;
    if (!confirm('将把云端书签还原到浏览器书签栏，并创建新文件夹。是否继续？')) return;
    setBackupRestoreLoading(true);
    setBackupRestoreError(null);
    const { error } = await restoreToBrowser(userId);
    setBackupRestoreLoading(false);
    if (error) {
      setBackupRestoreError(error);
    } else {
      alert('还原完成');
    }
  }, [userId]);

  const handleInstallExtension = useCallback(() => {
    navigate('/bookmarks/install');
  }, [navigate]);

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <p className="text-gray-600 dark:text-gray-400">请先登录后使用书签功能</p>
      </div>
    );
  }

  if (foldersLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 dark:bg-gray-900">
      <BookmarkSidebar
        folders={folders}
        selectedFolderId={folderId ?? null}
        onAddFolder={handleAddFolder}
      />
      <BookmarkContent
        folder={currentFolder}
        subfolders={subfolders}
        bookmarks={bookmarksToShow}
        bookmarksLoading={bookmarksLoading}
        onAddBookmark={handleAddBookmark}
        onEditBookmark={handleEditBookmark}
        onDeleteBookmark={handleDeleteBookmark}
        onAddSubfolder={handleAddSubfolderFromContent}
        onBackup={handleBackup}
        onRestore={handleRestore}
        onInstallExtension={handleInstallExtension}
        duplicateError={duplicateError}
        onClearDuplicateError={() => setDuplicateError(null)}
      />

      {backupRestoreError && (
        <div className="fixed bottom-4 left-4 right-4 z-40 mx-auto max-w-md rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-center text-sm text-red-800 dark:text-red-200 flex items-center justify-between gap-2">
          <span>{backupRestoreError}</span>
          <button type="button" onClick={() => setBackupRestoreError(null)} className="underline">
            关闭
          </button>
        </div>
      )}
      {backupRestoreLoading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30">
          <div className="rounded-xl bg-white dark:bg-gray-800 p-4 shadow-lg">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto" />
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">处理中...</p>
          </div>
        </div>
      )}

      {editingBookmark && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-4">
            <h3 className="text-lg font-semibold mb-4">编辑书签</h3>
            <BookmarkForm
              bookmark={editingBookmark}
              onSave={handleSaveEditBookmark}
              onCancel={() => setEditingBookmark(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BookmarksPage;
